<template>
  <v-container
    fluid
    class="dashboard-root"
    style="min-height: 100%; background: #131b2e"
  >
    <!-- Role-based rendering -->
    <div v-if="role === 'banker'">
      <!-- Banker Dashboard -->
      <div id="BankerDashboard">
        <v-app-bar dense flat color="transparent" class="dashboard-toolbar">
          <v-icon class="bank-icon mr-4" color="white" size="40">
            mdi-bank
          </v-icon>
          <div>
            <span class="toolbar-title text-h6 font-weight-bold">
              Banker
              <!-- - #{{ code }} -->
            </span>
            <div class="toolbar-status text-caption">
              <span class="status-dot"></span> Lobby #{{ code }}
            </div>
          </div>
          <v-spacer />
          <v-btn icon flat>
            <v-menu offset-y bottom left>
              <template #activator="{ props }">
                <v-btn icon v-bind="props" flat>
                  <v-icon color="white">mdi-dots-vertical</v-icon>
                </v-btn>
              </template>
              <v-list>
                <v-list-item
                  v-if="role === 'banker'"
                  @click="confirmEndDialog = true"
                >
                  <v-list-item-title>End Game</v-list-item-title>
                </v-list-item>
                <v-list-item
                  v-if="role === 'player'"
                  @click="confirmLeaveDialog = true"
                >
                  <v-list-item-title>Leave Game</v-list-item-title>
                </v-list-item>
                <v-list-item @click="showAboutGame">
                  <v-list-item-title>About Game</v-list-item-title>
                </v-list-item>
                <v-list-item
                  target="_blank"
                  href="https://mobipoly.netlify.app/"
                  link
                >
                  <v-list-item-title>Visit Us</v-list-item-title>
                </v-list-item>
                <v-list-item
                  target="_blank"
                  href="https://mobipoly.netlify.app/privacy-policy"
                  link
                >
                  <v-list-item-title>Privacy Policy</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </v-btn>
        </v-app-bar>

        <v-card class="dashboard-card pool-card">
          <div class="pool-card-title d-flex align-center">
            <span class="text-white font-weight-bold">Pool Management</span>
            <v-spacer />
            <v-chip class="text-white" style="font-size: 1em">
              ₹ {{ roomData.poolAmount.toLocaleString() }}
            </v-chip>
          </div>
          <v-text-field
            v-model="poolInput"
            class="pool-input mt-4 mb-4"
            placeholder="Enter Pool Amount"
            type="number"
            inputmode="numeric"
            variant="outlined"
            hide-details
            density="compact"
            flat
          />
          <div class="d-flex">
            <v-btn class="pool-btn pool-btn-reset mr-3" @click="resetPool">
              Reset
            </v-btn>
            <v-btn class="pool-btn pool-btn-add" @click="addPool">Add</v-btn>
          </div>
        </v-card>

        <v-divider color="white" class="my-6"></v-divider>

        <!-- Player List -->
        <div
          class="mb-2 ml-4 text-white font-weight-bold"
          style="font-size: 1.23em"
        >
          Players Details :
        </div>
        <div class="player-grid">
          <v-card
            v-if="roomData.players.length === 0"
            class="mb-4 pa-4"
            color="red"
            variant="tonal"
            dark
          >
            No players have joined yet.
          </v-card>
          <v-card
            v-for="player in roomData.players"
            :key="player.id"
            class="dashboard-card player-card mb-4"
          >
            <div class="d-flex align-center justify-space-between mb-2">
              <span class="font-weight-bold player-name"
                >{{ player.name }}
                <span
                  :style="{ color: player.color }"
                  class="mdi mdi-chess-rook"
                ></span
              ></span>
              <v-chip class="player-amount-chip" color="blue-grey-darken-4">
                ₹ {{ player.amount.toLocaleString() }}
              </v-chip>
            </div>
            <div class="player-id mb-2">ID: {{ player.id }}</div>
            <v-text-field
              v-model.number="player.poolInput"
              placeholder="Enter Pool Amount"
              class="player-input mb-2"
              variant="outlined"
              type="number"
              inputmode="numeric"
              hide-details
              density="compact"
              flat
            />
            <v-text-field
              v-model="player.reason"
              placeholder="Enter Reason"
              class="player-input mb-2"
              variant="outlined"
              hide-details
              density="compact"
              flat
            />
            <div class="d-flex">
              <v-btn
                class="player-btn player-btn-reset mr-2"
                @click="showResetPlayerDialog(player)"
              >
                Reset
              </v-btn>
              <v-btn
                class="player-btn player-btn-deduct mr-2"
                @click="showDeductPlayerDialog(player)"
              >
                Deduct
              </v-btn>
              <v-btn
                class="player-btn player-btn-add"
                @click="addToPlayer(player)"
              >
                Add
              </v-btn>
            </div>
          </v-card>
        </div>

        <v-divider color="white" class="my-6"></v-divider>

        <!-- Transaction History -->
        <v-card
          class="dashboard-card history-card"
          v-if="roomData.transactionHistory.length > 0"
        >
          <div
            class="mb-2 text-white font-weight-bold"
            style="font-size: 1.23em"
          >
            Transaction History :
          </div>
          <div class="history-list" style="font-size: 1.02em">
            <div
              v-for="(item, i) in roomData.transactionHistory"
              :key="i"
              :style="{ color: item.color }"
            >
              {{ item.text }}
            </div>
          </div>
        </v-card>
      </div>
    </div>

    <!-- Player Dashboard -->
    <div v-else-if="role === 'player'">
      <div id="PlayerDashboard">
        <v-app-bar dense flat color="transparent" class="dashboard-toolbar">
          <v-avatar size="40" class="mr-4" color="white"
            ><v-icon color="blue-grey-darken-4">mdi-account</v-icon></v-avatar
          >
          <div>
            <span class="toolbar-title text-h6">
              {{ player.name }}
              <span
                class="player-color text-caption ml-1"
                :style="{ color: player.color }"
              >
                <v-icon
                  class="mr-1"
                  style="
                     {
                      color: player.color;
                    }
                  "
                  >mdi-chess-rook</v-icon
                >{{ player.colorName }}
              </span>
            </span>
            <div class="toolbar-status text-caption">
              <span class="status-dot" />Lobby #{{ code }}
            </div>
          </div>
          <v-spacer />
          <v-btn icon flat>
            <v-menu offset-y bottom left>
              <template #activator="{ props }">
                <v-btn icon v-bind="props" flat>
                  <v-icon color="white">mdi-dots-vertical</v-icon>
                </v-btn>
              </template>
              <v-list>
                <v-list-item
                  v-if="role === 'player'"
                  @click="confirmExitDialog = true"
                >
                  <v-list-item-title>Leave Game</v-list-item-title>
                </v-list-item>
                <v-list-item @click="showAboutGame">
                  <v-list-item-title>About Game</v-list-item-title>
                </v-list-item>
                <v-list-item
                  target="_blank"
                  href="https://mobipoly.netlify.app/"
                  link
                >
                  <v-list-item-title>Visit Us</v-list-item-title>
                </v-list-item>
                <v-list-item
                  target="_blank"
                  href="https://mobipoly.netlify.app/privacy-policy"
                  link
                >
                  <v-list-item-title>Privacy Policy</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </v-btn>
        </v-app-bar>

        <v-card class="dashboard-card mb-6 player-main-card">
          <div class="d-flex align-center justify-space-between mb-2">
            <span class="font-weight-bold player-name">{{ player.name }}</span>
            <v-chip class="text-white" style="font-size: 1.18em">
              ₹ {{ player.amount.toLocaleString() }}
            </v-chip>
          </div>
          <div class="player-id mb-2">ID: {{ player.id }}</div>
          <v-select
            v-model="payForm.payee"
            :items="payees"
            item-title="name"
            item-value="id"
            class="player-input mb-2"
            variant="outlined"
            hide-details
            density="compact"
            placeholder="Choose Player / Banker"
            flat
          />
          <v-text-field
            v-model.number="payForm.amount"
            type="number"
            class="player-input mb-2"
            variant="outlined"
            hide-details
            density="compact"
            placeholder="Enter Amount"
            flat
          />
          <v-text-field
            v-model="payForm.reason"
            class="player-input mb-4"
            variant="outlined"
            hide-details
            density="compact"
            placeholder="Type the Reason"
            flat
          />
          <v-btn
            class="pay-btn"
            block
            :disabled="!payForm.payee || !payForm.amount"
            @click="onPay"
          >
            Pay
          </v-btn>
        </v-card>

        <v-card class="dashboard-card history-card">
          <div
            class="mb-2 text-white font-weight-bold"
            style="font-size: 1.23em"
          >
            Transaction History :
          </div>
          <div class="history-list" style="font-size: 1.02em">
            <div
              v-for="(item, i) in roomData.transactionHistory"
              :key="i"
              :style="{ color: item.color }"
            >
              {{ item.text }}
            </div>
          </div>
        </v-card>
      </div>
    </div>

    <div v-else>
      <p class="text-center text-white mt-10">
        Invalid Role: Please enter from Banker or Player dashboard
      </p>
    </div>

    <!-- Dialogs for confirmations -->
    <v-dialog v-model="confirmEndDialog" max-width="400">
      <v-card>
        <v-card-title class="text-h6">Confirmation</v-card-title>
        <v-card-text>Are you sure, Do you want to end the game?</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="confirmEndDialog = false">Cancel</v-btn>
          <v-btn color="red" text @click="confirmEndGame">Yes</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="confirmExitDialog" max-width="400">
      <v-card>
        <v-card-title class="text-h6">Confirmation</v-card-title>
        <v-card-text>Are you sure, Do you want to exit the game?</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="confirmExitDialog = false">Cancel</v-btn>
          <v-btn color="red" text @click="confirmLeaveGame">Yes</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="confirmResetPlayerDialog" max-width="400">
      <v-card>
        <v-card-title class="text-h6">Confirmation</v-card-title>
        <v-card-text>
          Are you sure you want to reset amount for
          <strong>{{ selectedPlayer?.name }}</strong
          >?
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="confirmResetPlayerDialog = false">Cancel</v-btn>
          <v-btn color="red" text @click="resetPlayerConfirmed">Yes</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="confirmDeductPlayerDialog" max-width="400">
      <v-card>
        <v-card-title class="text-h6">Confirmation</v-card-title>
        <v-card-text>
          Are you sure you want to deduct
          <strong>{{ selectedPlayer?.poolInput || 0 }}</strong> from
          <strong>{{ selectedPlayer?.name }}</strong
          >?
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="confirmDeductPlayerDialog = false">Cancel</v-btn>
          <v-btn color="red" text @click="deductPlayerConfirmed">Yes</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup>
import { ref, reactive, onMounted, onUnmounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import {
  doc,
  onSnapshot,
  updateDoc,
  arrayUnion,
  getDoc,
  deleteDoc,
} from "firebase/firestore";
import { db } from "@/firebase";
import { App as CapacitorApp } from "@capacitor/app";

const route = useRoute();
const router = useRouter();

const role = ref(route.query.role || "player");
const code = route.query.code || "";

const confirmEndDialog = ref(false);
const confirmLeaveDialog = ref(false);
const confirmResetPlayerDialog = ref(false);
const confirmDeductPlayerDialog = ref(false);

const confirmExitDialog = ref(false);

const selectedPlayer = ref(null);

const roomData = reactive({
  poolAmount: 0,
  players: [],
  transactionHistory: [],
});

const poolInput = ref("");

const payForm = reactive({
  payee: null,
  amount: "",
  reason: "",
});

const player = reactive({
  id: Number(route.query.playerId) || 0,
  name: route.query.playerName || "",
  color: route.query.playerColor || "red",
  get colorName() {
    const cmap = { red: "Red", blue: "Blue", green: "Green", yellow: "Yellow" };
    return cmap[this.color?.toLowerCase()] || this.color;
  },
  amount: 0,
});

const payees = ref([]);

const roomRef = doc(db, "rooms", code);

let unsubscribe = null;

async function removePlayerFromRoom(playerId) {
  const docSnap = await getDoc(roomRef);
  if (docSnap.exists()) {
    const data = docSnap.data();
    const filteredPlayers = (data.players || []).filter(
      (p) => p.id !== playerId
    );
    await updateDoc(roomRef, { players: filteredPlayers });
  }
}

onMounted(() => {
  if (!code) {
    alert("Room code is missing");
    router.push("/choosescreen");
    return;
  }

  unsubscribe = onSnapshot(roomRef, (docSnap) => {
    if (!docSnap.exists()) {
      // alert("Room does not exist or was closed.");
      //alert("The Session has ended.");
      router.push("/choosescreen");
      return;
    }
    const data = docSnap.data();
    roomData.poolAmount = data.poolAmount || 0;
    roomData.players = (data.players || []).map((p) => ({
      ...p,
      poolInput: "",
      reason: "",
      deductError: false,
      deductErrorMsg: "",
    }));
    roomData.transactionHistory = data.transactionHistory || [];

    // If there are no players, everyone (except banker) leaves the room page
    if (
      role.value === "player" &&
      !roomData.players.some((p) => p.id === player.id)
    ) {
      router.push("/choosescreen");
      return;
    }

    const currentPlayer = roomData.players.find((p) => p.id === player.id);
    if (currentPlayer) player.amount = currentPlayer.amount;

    if (role.value === "player") {
      payees.value = [
        { id: "banker", name: "Banker" },
        ...roomData.players.filter((p) => p.id !== player.id),
      ];
    } else {
      payees.value = [];
    }
  });
});

onUnmounted(() => {
  if (unsubscribe) unsubscribe();
});

async function updateRoom(fields) {
  await updateDoc(roomRef, fields);
}

async function addTransaction(text) {
  await updateDoc(roomRef, {
    transactionHistory: arrayUnion({
      text,
      color: text.includes("Deducted") ? "#FF6666" : "#00FF99",
      timestamp: Date.now(),
    }),
  });
}

async function addPool() {
  const amount = parseInt(poolInput.value);
  if (!amount || amount <= 0) return;
  const newPool = roomData.poolAmount + amount;
  await updateRoom({ poolAmount: newPool });
  await addTransaction(`Banker : Pool added ₹${amount.toLocaleString()}`);
  poolInput.value = "";
}

async function resetPool() {
  await updateRoom({ poolAmount: 0 });
  await addTransaction(`Banker : Pool reset to ₹0`);
}

async function updatePlayerAmount(id, amount) {
  const updatedPlayers = roomData.players.map((p) =>
    p.id === id ? { ...p, amount } : p
  );
  await updateRoom({ players: updatedPlayers });
}

function showResetPlayerDialog(player) {
  selectedPlayer.value = player;
  confirmResetPlayerDialog.value = true;
}

async function resetPlayerConfirmed() {
  if (!selectedPlayer.value) return;
  await updatePlayerAmount(selectedPlayer.value.id, 0);
  await addTransaction(
    `Banker : Reset amount for ${selectedPlayer.value.name}`
  );
  selectedPlayer.value.poolInput = "";
  selectedPlayer.value.reason = "";
  confirmResetPlayerDialog.value = false;
}

function showDeductPlayerDialog(player) {
  selectedPlayer.value = player;
  confirmDeductPlayerDialog.value = true;
}

async function deductPlayerConfirmed() {
  if (!selectedPlayer.value) return;
  const amount = parseInt(selectedPlayer.value.poolInput);
  if (!amount || amount <= 0) {
    confirmDeductPlayerDialog.value = false;
    return;
  }
  if (amount > selectedPlayer.value.amount) {
    selectedPlayer.value.deductError = true;
    selectedPlayer.value.deductErrorMsg = "Amount exceeds balance";
    confirmDeductPlayerDialog.value = false;
    return;
  }
  let newAmount = selectedPlayer.value.amount - amount;
  if (newAmount < 0) newAmount = 0;
  await updatePlayerAmount(selectedPlayer.value.id, newAmount);
  await addTransaction(
    `Banker : Deducted ₹${amount.toLocaleString()} from ${
      selectedPlayer.value.name
    }${selectedPlayer.value.reason ? " - " + selectedPlayer.value.reason : ""}`
  );
  selectedPlayer.value.poolInput = "";
  selectedPlayer.value.reason = "";
  confirmDeductPlayerDialog.value = false;
}

async function addToPlayer(player) {
  const amount = parseInt(player.poolInput);
  if (!amount || amount <= 0) return;
  if (amount > roomData.poolAmount) {
    alert("Pool does not have enough funds.");
    return;
  }
  const newPlayerAmount = player.amount + amount;
  const newPoolAmount = roomData.poolAmount - amount;
  const playersUpdated = roomData.players.map((p) =>
    p.id === player.id ? { ...p, amount: newPlayerAmount } : p
  );
  await updateRoom({ players: playersUpdated, poolAmount: newPoolAmount });
  await addTransaction(
    `Banker : Added ₹${amount.toLocaleString()} to ${player.name}${
      player.reason ? " - " + player.reason : ""
    }`
  );
  player.poolInput = "";
  player.reason = "";
}

async function onPay() {
  if (!payForm.payee || !payForm.amount) return;
  const amount = parseInt(payForm.amount);
  if (!amount || amount <= 0) return;
  if (player.amount < amount) {
    alert("Insufficient balance.");
    return;
  }
  player.amount -= amount;

  let playersUpdated = roomData.players.map((p) =>
    p.id === player.id ? { ...p, amount: player.amount } : p
  );
  if (payForm.payee === "banker") {
    // Add to pool
    const newPool = roomData.poolAmount + amount;
    await updateRoom({ poolAmount: newPool, players: playersUpdated });
  } else {
    const payee = roomData.players.find((p) => p.id === payForm.payee);
    if (payee) {
      playersUpdated = playersUpdated.map((p) =>
        p.id === payee.id ? { ...p, amount: p.amount + amount } : p
      );
      await updateRoom({ players: playersUpdated });
    }
  }
  await addTransaction(
    `${player.name} paid ₹${amount.toLocaleString()} to ${
      payForm.payee === "banker"
        ? "Banker"
        : roomData.players.find((p) => p.id === payForm.payee)?.name ||
          "Unknown"
    }${payForm.reason ? " - " + payForm.reason : ""}`
  );
  payForm.payee = null;
  payForm.amount = "";
  payForm.reason = "";
}

async function leaveGameConfirmed() {
  await removePlayerFromRoom(player.id);
  confirmExitDialog.value = false;
  confirmLeaveDialog.value = false;
  router.push("/choosescreen");
}

function confirmLeaveGame() {
  leaveGameConfirmed();
}

// DELETE THE ENTIRE ROOM WHEN BANKER ENDS THE GAME
async function confirmEndGame() {
  await deleteDoc(roomRef);
  confirmEndDialog.value = false;
  router.push("/choosescreen");
}

// function setupBackButtonHandler() {
//   const backButtonHandler = CapacitorApp.addListener("backButton", () => {
//     if (role.value === "banker") {
//       confirmEndDialog.value = true;
//     } else if (role.value === "player") {
//       confirmExitDialog.value = true;
//     }
//     // Prevent default back action (exiting or navigating back)
//   });

//   // Clean up listener on unmount
//   onUnmounted(() => {
//     backButtonHandler.remove();
//   });
// }

// onMounted(() => {
//   setupBackButtonHandler();
// });

let removeBackButtonListener = null;

function setupBackButtonHandler() {
  // Mobile back button listener (Capacitor)
  removeBackButtonListener = CapacitorApp.addListener("backButton", () => {
    if (role.value === "banker") {
      confirmEndDialog.value = true;
    } else if (role.value === "player") {
      confirmExitDialog.value = true;
    }
  });

  // Browser back button listener
  const onPopState = () => {
    if (role.value === "banker") {
      confirmEndDialog.value = true;
      history.pushState(null, null, location.href);
    } else if (role.value === "player") {
      confirmExitDialog.value = true;
      history.pushState(null, null, location.href);
    }
  };

  window.addEventListener("popstate", onPopState);
  // Push initial state to history (for popstate event)
  history.pushState(null, null, location.href);

  // Clean up on unmount
  onUnmounted(() => {
    if (typeof removeBackButtonListener === "function") {
      removeBackButtonListener();
    }
    window.removeEventListener("popstate", onPopState);
  });
}

onMounted(() => {
  setupBackButtonHandler();
});

function showAboutGame() {
  alert("Mobipoly Game\nVersion 1.0.0\nDeveloped by Lebin Jose");
}
</script>

<style scoped>
/*.dashboard-root {
  padding-bottom: 32px;
} */

.dashboard-toolbar {
  background: #151f35 !important;
  margin-bottom: 28px;
  box-shadow: 0 6px 20px 0 rgba(0, 0, 0, 0.08);
  padding-left: 16px;
}

.toolbar-title {
  color: #fff;
  font-size: 1.4em;
  letter-spacing: 0.04em;
}

.toolbar-status {
  color: #fff;
  font-size: 1em;
  font-weight: 500;
  display: flex;
  align-items: center;
}

.status-dot {
  display: inline-block;
  width: 11px;
  height: 11px;
  border-radius: 50%;
  background: #34eb7d;
  margin-right: 8px;
}

/* .bank-icon {
  background: #263452;
  border-radius: 10px;
  padding: 6px;
} */

.dashboard-card {
  background: #21294b !important;
  border-radius: 14px !important;
  margin-bottom: 8px;
  padding: 20px 20px 16px 20px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.09);
}

.pool-card-title {
  font-size: 1.18em;
  margin-bottom: -4px;
}

.pool-input,
.player-input {
  background: rgba(255, 255, 255, 0.06) !important;
  border-radius: 7px;
  color: #fff;
}

.pool-btn,
.player-btn {
  min-width: 70px;
  border-radius: 7px;
  font-size: 0.97em;
  text-transform: none;
  color: #fff;
  box-shadow: none !important;
}

.pool-btn-reset,
.player-btn-reset {
  background: linear-gradient(90deg, #c48629 0%, #99900d 100%);
  color: #fff;
}

.pool-btn-add,
.player-btn-add {
  background: linear-gradient(90deg, #019575 0%, #07864d 100%);
  color: #fff;
}

.player-btn-deduct {
  background: linear-gradient(90deg, #ff5858 0%, #fb8d34 100%);
  color: #fff;
}

.player-amount-chip {
  background: #37446b !important;
  color: #fff !important;
  font-size: 1.08em !important;
}

.player-name {
  font-size: 1.14em;
  color: #fff;
  letter-spacing: 0.01em;
}

.player-id {
  font-size: 0.95em;
  color: #b9bed4;
}

.player-card:last-child {
  margin-right: 0;
}

.player-grid {
  width: 100%;
  max-width: 850px;
  margin: 0 auto;
}

.history-card {
  padding-bottom: 18px !important;
}

.history-list {
  padding-left: 6px;
  max-height: 300px;
  overflow: auto;
}

.player-main-card {
  max-width: 480px;
  margin: 0 auto 28px auto;
}

.pay-btn {
  background: linear-gradient(90deg, #6a73f7 0%, #67d9f7 100%) !important;
  color: #fff !important;
  border-radius: 7px;
  font-size: 1.09em;
  font-weight: 650;
  text-transform: none;
  box-shadow: none !important;
}

.player-color {
  font-size: 1em;
  font-weight: 600;
}
</style>
